import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ROLE_INFO, SCORE_LABELS, type GameLogDto, type GameParticipantDto, type RoleId, type ScoreReason, type Winner } from '@mafia/core';
import { PHASE_NAMES, teamName } from '@/lib/format';
import { Card } from '@/components/ui';

type Entry = GameLogDto['log'][number];
type Tone = 'kill' | 'save' | 'check' | 'vote' | 'meta' | 'normal';

const CONNECTION_EVENTS = new Set(['PLAYER_DISCONNECTED', 'PLAYER_RECONNECTED']);
const SCORE_EVENTS = new Set(['SCORE']);
/** Служебные события, которые игрокам неинтересны. */
const TECH_EVENTS = new Set(['MODERATOR_ADVANCE', 'ROLE_ASSIGNED']);

/**
 * Хронология партии по ночам и дням: кто в кого стрелял, кого лечили и проверяли,
 * кто кого выставил и как голосовал. Общая для админки и страницы итогов у игроков.
 */
export function MatchTimeline({ game, technical = false }: { game: GameLogDto; technical?: boolean }) {
  const [showConnections, setShowConnections] = useState(false);
  const [showScores, setShowScores] = useState(technical);

  const sections = useMemo(() => {
    const bySeat = new Map(game.participants.map((p) => [p.seat, p]));
    const out: { title: string; night: boolean; lines: { at: string; text: string; tone: Tone }[] }[] = [];
    const start = new Date(game.startedAt).getTime();
    for (const e of game.log) {
      if (e.type === 'PHASE_STARTED') {
        if (e.phase === 'GAME_OVER') continue;
        out.push({
          title: e.day > 0 ? `${e.phase === 'NIGHT' ? 'Ночь' : 'День'} ${e.day} · ${PHASE_NAMES[e.phase]}` : PHASE_NAMES[e.phase],
          night: e.phase === 'NIGHT',
          lines: [],
        });
        continue;
      }
      if (!showConnections && CONNECTION_EVENTS.has(e.type)) continue;
      if (!showScores && SCORE_EVENTS.has(e.type)) continue;
      if (!technical && TECH_EVENTS.has(e.type)) continue;
      const line = describe(e, bySeat);
      if (!line) continue;
      if (!out.length) out.push({ title: 'Начало', night: false, lines: [] });
      const secs = Math.max(0, Math.round((new Date(e.at).getTime() - start) / 1000));
      out.at(-1)!.lines.push({ at: `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`, ...line });
    }
    return out.filter((s) => s.lines.length > 0);
  }, [game, showConnections, showScores, technical]);

  return (
    <Card className="p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg">Хронология партии</h2>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" className="accent-gold-400" checked={showScores} onChange={(e) => setShowScores(e.target.checked)} />
            Баллы
          </label>
          {technical && (
            <label className="flex items-center gap-2 text-sm text-zinc-400">
              <input type="checkbox" className="accent-gold-400" checked={showConnections} onChange={(e) => setShowConnections(e.target.checked)} />
              Подключения
            </label>
          )}
        </div>
      </div>
      <div className="space-y-5">
        {sections.map((s, i) => (
          <section key={i}>
            <h3 className={clsx('mb-2 text-xs font-semibold uppercase tracking-wider', s.night ? 'text-indigo-300' : 'text-gold-300')}>{s.title}</h3>
            <ol className="space-y-1 border-l border-white/10 pl-4">
              {s.lines.map((l, j) => (
                <li key={j} className="flex gap-3 text-sm">
                  <span className="w-10 shrink-0 font-mono text-xs leading-5 text-zinc-600">{l.at}</span>
                  <span
                    className={clsx(
                      l.tone === 'kill' && 'text-blood-400',
                      l.tone === 'save' && 'text-emerald-300',
                      l.tone === 'check' && 'text-sky-300',
                      l.tone === 'vote' && 'text-zinc-300',
                      l.tone === 'meta' && 'text-zinc-500',
                    )}
                  >
                    {l.text}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    </Card>
  );
}

function describe(e: Entry, bySeat: Map<number, GameParticipantDto>): { text: string; tone: Tone } | null {
  const who = (seat: number | null) => {
    if (seat === null) return '—';
    const p = bySeat.get(seat);
    return p ? `№${seat} ${p.nickname} (${ROLE_INFO[p.role].name})` : `№${seat}`;
  };
  const payload = (e.payload ?? {}) as Record<string, unknown>;
  // Старые записи могли хранить одиночную строку вместо массива.
  const byRaw = payload.by;
  const by = (Array.isArray(byRaw) ? byRaw : byRaw ? [byRaw] : []).map((b) => (b === 'MAFIA' ? 'мафия' : b === 'LOVER' ? 'любовница' : 'маньяк')).join(' и ');

  switch (e.type) {
    case 'GAME_STARTED':
      return { text: `Игра началась, игроков: ${payload.playerCount}`, tone: 'meta' };
    case 'ROLE_ASSIGNED':
      return { text: `№${e.actorSeat} ${bySeat.get(e.actorSeat!)?.nickname ?? ''} получил роль «${ROLE_INFO[payload.role as RoleId].name}»`, tone: 'meta' };
    case 'MAFIA_VOTE':
      return payload.noKill
        ? { text: `${who(e.actorSeat)} голосует «не убивать»`, tone: 'meta' }
        : { text: `${who(e.actorSeat)} целится в ${who(e.targetSeat)}`, tone: 'kill' };
    case 'MAFIA_MISS':
      return {
        text:
          payload.reason === 'NO_KILL'
            ? 'Мафия решила никого не убивать'
            : payload.reason === 'TIE'
              ? 'Мафия не договорилась — промах'
              : 'Мафия никого не выбрала',
        tone: 'meta',
      };
    case 'DON_CHECK':
      return { text: `${who(e.actorSeat)} проверил ${who(e.targetSeat)}: ${payload.positive ? 'шериф' : 'не шериф'}`, tone: 'check' };
    case 'SHERIFF_CHECK':
      return { text: `${who(e.actorSeat)} проверил ${who(e.targetSeat)}: ${payload.positive ? 'мафия' : 'не мафия'}`, tone: 'check' };
    case 'DOCTOR_HEAL':
      return { text: `${who(e.actorSeat)} лечит ${who(e.targetSeat)}`, tone: 'save' };
    case 'MANIAC_TARGET':
      return { text: `${who(e.actorSeat)} выбрал жертву ${who(e.targetSeat)}`, tone: 'kill' };
    case 'SAVED':
      return payload.savedBy === 'LOVER'
        ? { text: `Любовница спасла ${who(e.targetSeat)}: мафия пришла к её гостю`, tone: 'save' }
        : { text: `Доктор спас ${who(e.targetSeat)} (стрелял: ${by})`, tone: 'save' };
    case 'LOVER_VISIT':
      return { text: `${who(e.actorSeat)} проводит ночь с ${who(e.targetSeat)}`, tone: 'normal' };
    case 'VOTE_SKIPPED':
      return { text: 'Стол пропустил голосование (раз за игру)', tone: 'meta' };
    case 'MODERATOR_ADVANCE':
      return { text: 'Ведущий переключил фазу', tone: 'meta' };
    case 'SCORE': {
      const pts = Number(payload.points);
      return {
        text: `${who(e.actorSeat)}: ${pts > 0 ? '+' : ''}${pts} — ${SCORE_LABELS[payload.reason as ScoreReason] ?? String(payload.reason)}`,
        tone: 'check',
      };
    }
    case 'KILLED':
      return {
        text: payload.withLover
          ? `${who(e.targetSeat)} погиб вместе с любовницей (к ней пришла мафия)`
          : by === 'любовница'
            ? `${who(e.targetSeat)} погиб из-за ночи с любовницей`
            : `${who(e.targetSeat)} убит ночью (${by})`,
        tone: 'kill',
      };
    case 'NOMINATED':
      return payload.replaced
        ? { text: `${who(e.actorSeat)} передумал: выставил ${who(e.targetSeat)} вместо ${who(Number(payload.replaced))}`, tone: 'vote' }
        : { text: `${who(e.actorSeat)} выставил ${who(e.targetSeat)}`, tone: 'vote' };
    case 'VOTE_CAST': {
      const verb = payload.changed ? 'меняет голос:' : 'голосует';
      return payload.skip
        ? { text: `${who(e.actorSeat)} ${verb} за пропуск голосования`, tone: 'vote' }
        : { text: `${who(e.actorSeat)} ${verb} против ${who(e.targetSeat)}`, tone: 'vote' };
    }
    case 'VOTE_RESULT': {
      const tally = Object.entries((payload.tally as Record<string, number>) ?? {})
        .map(([s, n]) => (s === '0' ? `пропуск: ${n}` : `№${s}: ${n}`))
        .join(', ');
      const outcome = e.targetSeat ? `исключён ${who(e.targetSeat)}` : payload.skipped ? 'пропуск' : payload.tie ? 'ничья' : 'никто не исключён';
      return { text: `Итог голосования${tally ? ` (${tally})` : ''} — ${outcome}`, tone: 'meta' };
    }
    case 'ELIMINATED':
      return { text: `${who(e.targetSeat)} покидает стол по итогам голосования`, tone: 'kill' };
    case 'PLAYER_LEFT':
      return { text: `${who(e.actorSeat)} ${payload.kicked ? 'удалён ведущим' : 'вышел из игры'}`, tone: 'meta' };
    case 'PLAYER_DISCONNECTED':
      return { text: `${who(e.actorSeat)} отключился`, tone: 'meta' };
    case 'PLAYER_RECONNECTED':
      return { text: `${who(e.actorSeat)} переподключился`, tone: 'meta' };
    case 'GAME_ENDED':
      return { text: `Игра окончена: ${payload.winner === 'DRAW' ? 'ничья' : `победа — ${teamName[payload.winner as Winner]}`}`, tone: 'save' };
    default:
      return null;
  }
}
